import React, { useState, useEffect } from "react";
import axios from "axios";
import DynamicSelect from "../DynamicSelect";
import { toast } from "react-toastify";

const API_URL = "https://api.muktihospital.com/api/ambulance";
const API_KEY = "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";

const AddAmbulanceForm = ({ initialData, onSuccess, onCancel }) => {
  const [formData, setFormData] = useState({
    vehicleNumber: "",
    vehicleModel: "",
    yearMade: "",
    driverName: "",
    driverLicense: "",
    driverContact: "",
    vehicleType: null,
    note: "",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (initialData) {
      setFormData({
        vehicleNumber: initialData.vehicleNumber || "",
        vehicleModel: initialData.vehicleModel || "",
        yearMade: initialData.yearMade || "",
        driverName: initialData.driverName || "",
        driverLicense: initialData.driverLicense || "",
        driverContact: initialData.driverContact || "",
        vehicleType: initialData.vehicleType
          ? { value: initialData.vehicleType, label: initialData.vehicleType }
          : null,
        note: initialData.note || "",
      });
    }
  }, [initialData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (selectedOption) => {
    setFormData((prev) => ({ ...prev, vehicleType: selectedOption }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Validate required fields
    if (!formData.vehicleNumber || !formData.vehicleModel) {
      toast.error("Please fill in all required fields.");
      setLoading(false);
      return;
    }

    const currentTimestamp = new Date().toISOString();
    const payload = {
      ...formData,
      vehicleType: formData.vehicleType?.value || "",
      yearMade: parseInt(formData.yearMade) || null,
      createdAt: initialData?.createdAt || currentTimestamp, // Set createdAt for new ambulances
      updatedAt: currentTimestamp, // Update updatedAt for both new and edited
    };

    try {
      let response;
      if (initialData?.id) {
        response = await axios.put(`${API_URL}/${initialData.id}`, payload, {
          headers: { "x-api-key": API_KEY },
        });
      } else {
        response = await axios.post(API_URL, payload, {
          headers: { "x-api-key": API_KEY },
        });
      }
      onSuccess?.(response.data);
    } catch (error) {
      console.error("Error saving ambulance:", error);
      toast.error(initialData ? "Failed to update ambulance." : "Failed to add ambulance.");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      vehicleNumber: "",
      vehicleModel: "",
      yearMade: "",
      driverName: "",
      driverLicense: "",
      driverContact: "",
      vehicleType: null,
      note: "",
    });
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 px-4 sm:px-0"
    >
      <div className="bg-white rounded-lg shadow-lg w-full max-w-[95%] sm:max-w-[80%] md:max-w-[700px] lg:max-w-[800px] max-h-[90vh] overflow-y-auto">
        <div className="p-4 sm:p-6 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-lg sm:text-xl font-bold text-gray-700">
            {initialData ? "Edit Ambulance Details" : "Add Ambulance Details"}
          </h2>
          <button
            type="button"
            className="text-gray-500 hover:text-gray-800 text-lg sm:text-xl"
            onClick={() => {
              resetForm();
              onCancel?.();
            }}
          >
            ✕
          </button>
        </div>

        <div className="p-4 sm:p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Vehicle Number <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="vehicleNumber"
              className="w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-gray-700"
              value={formData.vehicleNumber}
              onChange={handleChange}
              required
              placeholder="Enter Vehicle Number"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Vehicle Model <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="vehicleModel"
              className="w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-gray-700"
              value={formData.vehicleModel}
              onChange={handleChange}
              required
              placeholder="Enter Vehicle Model"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Year Made
            </label>
            <input
              type="number"
              name="yearMade"
              className="w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-gray-700"
              value={formData.yearMade}
              onChange={handleChange}
              placeholder="Enter Year Made"
              min="1900"
              max={new Date().getFullYear()}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Driver Name
            </label>
            <input
              type="text"
              name="driverName"
              className="w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-gray-700"
              value={formData.driverName}
              onChange={handleChange}
              placeholder="Enter Driver Name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Driver License
            </label>
            <input
              type="text"
              name="driverLicense"
              className="w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-gray-700"
              value={formData.driverLicense}
              onChange={handleChange}
              placeholder="Enter Driver License"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Driver Contact
            </label>
            <input
              type="text"
              name="driverContact"
              className="w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-gray-700"
              value={formData.driverContact}
              onChange={handleChange}
              placeholder="Enter Driver Contact"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Vehicle Type
            </label>
            <DynamicSelect
              options={[
                { value: "Own", label: "Own" },
                { value: "Contractual", label: "Contractual" },
              ]}
              value={formData.vehicleType}
              onChange={handleSelectChange}
              placeholder="Select Vehicle Type"
              isSearchable={true}
              isClearable
            />
          </div>
          <div className="sm:col-span-2 lg:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Note
            </label>
            <textarea
              name="note"
              className="w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-gray-700"
              value={formData.note}
              onChange={handleChange}
              placeholder="Enter any note"
              rows={4}
            />
          </div>
        </div>

        <div className="flex flex-col sm:flex-row justify-end gap-3 p-4 sm:p-6 border-t border-gray-200">
          <button
            type="button"
            className="bg-[#E7633D]/90 text-sm text-white py-2 px-6 rounded-md hover:bg-[#E7633D] transition-all duration-300 w-full sm:w-auto"
            onClick={() => {
              resetForm();
              onCancel?.();
            }}
          >
            {initialData ? "Cancel" : "Clear"}
          </button>
          <button
            type="submit"
            className="bg-M-primary-color/90 text-sm text-white py-2 px-6 rounded-md hover:bg-M-primary-color transition-all duration-200 w-full sm:w-auto"
            disabled={loading}
          >
            {loading ? "Saving..." : initialData ? "Update" : "Save"}
          </button>
        </div>
      </div>
    </form>
  );
};

export default AddAmbulanceForm;